/**
 * P0-DISCOVERY-05 — Nation momentum line.
 *
 * Re-uses the EXISTING nation_scores query (same one as NationPositionBadge)
 * and a localStorage "last seen rank" snapshot to derive momentum text
 * — no new tables, no new APIs, no new cron jobs.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { trackEvent } from "@/lib/audit";

interface Row {
  nation_code: string;
  nation_name: string;
}

function snapshotKey(nation: string) {
  return `vs:nation:rankSnapshot:${nation}`;
}

interface Snapshot {
  rank: number;
  ts: number;
}

function loadSnapshot(nation: string): Snapshot | null {
  try {
    const raw = window.localStorage.getItem(snapshotKey(nation));
    return raw ? (JSON.parse(raw) as Snapshot) : null;
  } catch {
    return null;
  }
}

function saveSnapshot(nation: string, snap: Snapshot) {
  try {
    window.localStorage.setItem(snapshotKey(nation), JSON.stringify(snap));
  } catch {
    /* noop */
  }
}

export function NationMomentumLine() {
  const { nation } = useSelectedNation();
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    if (!nation) return;
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("nation_scores")
        .select("nation_code,nation_name")
        .order("total_points", { ascending: false });
      if (!alive) return;
      const rows = (data ?? []) as Row[];
      const idx = rows.findIndex((r) => r.nation_code === nation);
      if (idx < 0) return;
      const rank = idx + 1;
      const me = rows[idx];
      const name = me?.nation_name ?? nation;
      const prev = loadSnapshot(nation);
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      let msg: string;
      if (prev && Date.now() - prev.ts < weekMs && prev.rank !== rank) {
        const diff = prev.rank - rank;
        if (diff > 0) {
          msg = `${name} subió ${diff} posición${diff === 1 ? "" : "es"} esta semana`;
        } else {
          msg = `${name} bajó ${Math.abs(diff)} posición${diff === -1 ? "" : "es"} esta semana`;
        }
      } else {
        msg = rank === 1
          ? `${name} mantiene el #1`
          : `${name} mantiene el #${rank}`;
      }
      // Refresh snapshot when stale (weekly cadence).
      if (!prev || Date.now() - prev.ts >= weekMs) {
        saveSnapshot(nation, { rank, ts: Date.now() });
      }
      setText(msg);
      void trackEvent("nation_momentum_shown", { nation, rank });
    })();
    return () => {
      alive = false;
    };
  }, [nation]);

  if (!text) return null;
  return (
    <div className="text-center text-[11px] font-bold text-muted-foreground">
      {text}
    </div>
  );
}
